import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingHistory = ({ user }) => {
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // Mock billing history data
  const billingHistory = [
    {
      id: 'inv_001',
      date: '2025-01-09',
      amount: 19.99,
      status: 'paid',
      description: 'CareerLift Premium - Monthly',
      invoiceUrl: '#'
    },
    {
      id: 'inv_002',
      date: '2024-12-09',
      amount: 19.99,
      status: 'paid',
      description: 'CareerLift Premium - Monthly',
      invoiceUrl: '#'
    },
    {
      id: 'inv_003',
      date: '2024-11-09',
      amount: 19.99,
      status: 'paid',
      description: 'CareerLift Premium - Monthly',
      invoiceUrl: '#'
    },
    {
      id: 'inv_004',
      date: '2024-10-09',
      amount: 19.99,
      status: 'failed',
      description: 'CareerLift Premium - Monthly',
      invoiceUrl: '#'
    }
  ];

  const handleDownloadInvoice = async (invoiceId) => {
    setDownloadingInvoice(invoiceId);
    // Simulate download delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    setDownloadingInvoice(null);
    // In real implementation, this would trigger actual download
    console.log('Downloading invoice:', invoiceId);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        color: 'text-success',
        bg: 'bg-success/10',
        icon: 'CheckCircle',
        text: 'Paid'
      },
      failed: {
        color: 'text-error',
        bg: 'bg-error/10',
        icon: 'XCircle',
        text: 'Failed'
      },
      pending: {
        color: 'text-warning',
        bg: 'bg-warning/10',
        icon: 'Clock',
        text: 'Pending'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.text}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user?.isPremium) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 text-center">
        <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Billing History</h3>
        <p className="text-muted-foreground mb-4">
          Upgrade to Premium to view your billing history and manage invoices.
        </p>
        <Button variant="outline" iconName="Crown" iconPosition="left">
          Upgrade to Premium
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Billing History</h3>
            <p className="text-muted-foreground text-sm">
              View and download your past invoices
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export All
          </Button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {billingHistory?.map((invoice) => (
          <div key={invoice?.id} className="p-6 hover:bg-muted/50 transition-colors duration-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Receipt" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{invoice?.description}</h4>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(invoice?.date)} • Invoice #{invoice?.id?.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="font-semibold text-foreground">
                    ${invoice?.amount?.toFixed(2)}
                  </div>
                  {getStatusBadge(invoice?.status)}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  loading={downloadingInvoice === invoice?.id}
                  onClick={() => handleDownloadInvoice(invoice?.id)}
                  iconName="Download"
                  iconPosition="left"
                >
                  Download
                </Button>
              </div>
            </div>

            {invoice?.status === 'failed' && (
              <div className="mt-3 p-3 bg-error/5 border border-error/20 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-error mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-error font-medium">Payment Failed</p>
                    <p className="text-xs text-error/80 mt-1">
                      Your payment method was declined. Please update your payment information.
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    Retry Payment
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {billingHistory?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No Invoices Yet</h4>
          <p className="text-muted-foreground">
            Your billing history will appear here after your first payment.
          </p>
        </div>
      )}
    </div>
  );
};

export default BillingHistory;